import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {Popper} from '~/components/Popper';
import type {Placement} from '@popperjs/core/lib/enums';
import type {PositioningStrategy} from '@popperjs/core/lib/types';
import style from './index.module.scss';

export type InfoPopoverProps = {
    content: ReactNode;
    children?: ReactNode;
    showIcon?: boolean;
    clickable?: boolean;
    ariaLabel?: string;
    className?: string;
    width?: number | string;
    placement?: Placement;
    strategy?: PositioningStrategy;
};

export const InfoPopover = ({
    content,
    children,
    showIcon = true,
    clickable = false,
    ariaLabel = 'More info',
    width = 320,
    placement = 'top',
    strategy = 'fixed',
    className
}: InfoPopoverProps) => {
    const triggerRef = useRef<HTMLSpanElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const closeTimerRef = useRef<number | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isPinned, setIsPinned] = useState(false);
    const contentStyle = useMemo(() => {
        const baseStyle = {
            pointerEvents: clickable ? 'auto' : 'none',
        } as const;
        if (typeof width === 'number') {
            return {...baseStyle, width: `${width}px`};
        }
        return {...baseStyle, width};
    }, [width, clickable]);

    const clearCloseTimer = () => {
        if (closeTimerRef.current !== null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const scheduleClose = ({respectPin = true}: {respectPin?: boolean} = {}) => {
        if (respectPin && isPinned) {
            return;
        }
        clearCloseTimer();
        closeTimerRef.current = window.setTimeout(() => {
            setIsOpen(false);
            setIsPinned(false);
        }, 120);
    };

    useEffect(() => () => clearCloseTimer(), []);

    const open = () => {
        clearCloseTimer();
        setIsOpen(true);
    };

    const close = () => {
        clearCloseTimer();
        setIsOpen(false);
        setIsPinned(false);
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onPointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;
            if (!target) {
                return;
            }

            if (triggerRef.current?.contains(target)) {
                return;
            }
            if (contentRef.current?.contains(target)) {
                return;
            }

            close();
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen]);

    const togglePinned = () => {
        clearCloseTimer();
        if (!isOpen) {
            setIsPinned(true);
            open();
            return;
        }

        if (!isPinned) {
            setIsPinned(true);
            return;
        }

        close();
    };

    return (
        <>
            <span
                ref={triggerRef}
                aria-label={ariaLabel}
                aria-expanded={isOpen}
                role="button"
                tabIndex={0}
                className={[style.trigger, className].filter(Boolean).join(' ')}
                onMouseEnter={open}
                onMouseLeave={() => scheduleClose()}
                onFocus={open}
                onBlur={(event) => {
                    const nextTarget = event.relatedTarget as Node | null;
                    if (nextTarget && contentRef.current?.contains(nextTarget)) {
                        return;
                    }
                    scheduleClose({respectPin: false});
                }}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    togglePinned();
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        togglePinned();
                    }
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        close();
                    }
                }}
            >
                {children}
                {(!children || showIcon) && <i className={['fa fa-info-circle', style.icon].join(' ')} aria-hidden="true" />}
            </span>
            {isOpen && (
                <Popper
                    parentRef={triggerRef}
                    placement={placement}
                    strategy={strategy}
                    modifiers={[{name: 'flip', enabled: false}]}
                    popperStyle={{pointerEvents: clickable ? 'auto' : 'none'}}
                >
                    <div
                        ref={contentRef}
                        onMouseEnter={open}
                        onMouseLeave={() => scheduleClose()}
                        onFocusCapture={open}
                        onBlurCapture={(event) => {
                            const nextTarget = event.relatedTarget as Node | null;
                            if (nextTarget && triggerRef.current?.contains(nextTarget)) {
                                return;
                            }
                            scheduleClose({respectPin: false});
                        }}
                        className={style.content}
                        style={contentStyle}
                    >
                        {content}
                    </div>
                </Popper>
            )}
        </>
    );
};
